import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../ui/card';
import { Activity, Eye, MousePointer, FileText, ArrowRight, LogOut } from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

const ActivityFeed = () => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchActivities();
    const interval = setInterval(fetchActivities, 5000); // Refresh every 5 seconds
    return () => clearInterval(interval);
  }, []);

  const fetchActivities = async () => {
    try {
      const token = localStorage.getItem('customerAdminToken');
      const response = await fetch(`${API_BASE_URL}/api/traffic/activity-logs?limit=50`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setActivities(data.logs || []);
      }
    } catch (error) {
      console.error('Error fetching activities:', error);
    } finally {
      setLoading(false);
    }
  };

  const getActivityIcon = (activityType) => {
    switch(activityType?.toLowerCase()) {
      case 'pageview': return <Eye className="h-4 w-4 text-blue-600" />;
      case 'click': return <MousePointer className="h-4 w-4 text-purple-600" />;
      case 'form_submit': return <FileText className="h-4 w-4 text-green-600" />;
      case 'scroll': return <ArrowRight className="h-4 w-4 text-orange-600" />;
      case 'page_exit': return <LogOut className="h-4 w-4 text-red-600" />;
      default: return <Activity className="h-4 w-4 text-gray-600" />;
    }
  };

  const getActivityColor = (activityType) => {
    switch(activityType?.toLowerCase()) {
      case 'pageview': return 'bg-blue-50 border-blue-200';
      case 'click': return 'bg-purple-50 border-purple-200';
      case 'form_submit': return 'bg-green-50 border-green-200';
      case 'scroll': return 'bg-orange-50 border-orange-200';
      case 'page_exit': return 'bg-red-50 border-red-200';
      default: return 'bg-gray-50 border-gray-200';
    }
  };

  const formatTime = (timestamp) => {
    // If timestamp is string, parse it. If already Date, use as-is
    const date = typeof timestamp === 'string' ? new Date(timestamp) : timestamp;
    
    // Convert to India timezone (Asia/Kolkata)
    return date.toLocaleTimeString('en-IN', { 
      hour: '2-digit', 
      minute: '2-digit', 
      second: '2-digit',
      timeZone: 'Asia/Kolkata',
      hour12: true
    });
  };

  const getActivityDescription = (activity) => {
    switch(activity.activityType?.toLowerCase()) {
      case 'pageview':
        return `Viewed page: ${activity.pageTitle || activity.pageUrl?.substring(0, 40)}`;
      case 'click':
        return `Clicked ${activity.element}: "${activity.text?.substring(0, 30)}"`;
      case 'form_submit':
        return `Submitted form: ${activity.formId}`;
      case 'scroll':
        return `Scrolled to ${activity.depth}% of page`;
      case 'page_exit':
        return `Left page: ${activity.exitPage?.substring(0, 40)}`;
      default:
        return activity.activityType;
    }
  };

  return (
    <Card className="h-[500px]">
      <CardContent className="p-4 h-full flex flex-col">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <Activity className="h-5 w-5 text-emerald-600 animate-pulse" />
            Live Activity Feed
          </h3>
          <span className="text-xs text-gray-500">Auto-refreshing</span>
        </div>

        <div className="flex-1 overflow-y-auto space-y-2">
          {loading ? (
            <div className="flex justify-center items-center h-full">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
            </div>
          ) : activities.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Activity className="h-12 w-12 mx-auto mb-3 text-gray-400" />
              <p>No activities yet</p>
            </div>
          ) : (
            activities.map((activity, index) => (
              <div
                key={index}
                className={`p-3 rounded-lg border-l-4 ${getActivityColor(activity.activityType)} transition-all hover:shadow-sm`}
              >
                <div className="flex items-start gap-3">
                  <div className="mt-1">
                    {getActivityIcon(activity.activityType)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-900 font-medium">
                      {getActivityDescription(activity)}
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs text-gray-500">
                        {formatTime(activity.timestamp)}
                      </span>
                      <span className="text-xs text-gray-400">•</span>
                      <span className="text-xs text-gray-500 truncate">
                        Session: {activity.sessionId?.substring(0, 10)}...
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default ActivityFeed;